import {
  ITEM_PATH,
  INVALID_URL,
  IMPORT_PATH,
  ITEM_EDIT_PATH,
} from 'constants/general';
import avatar from 'img/picture.png';
import { Tab } from 'components/Tab/';
import { history } from 'store/store';
import { useCheckTable } from 'hooks/';
import { Table } from 'components/Table/';
import { disabled } from 'constants/status';
import {
  StyledName,
  ActionButton,
  StyledItemTag,
  StyledListWrapper,
  StyledTableWrapper,
} from 'components/Item/Record/Record.styled';
import Avatar from 'components/Avatar/Avatar';
import React, { useRef, useMemo } from 'react';
import { Animation } from 'components/Loaders/';
import { TableButton } from 'components/Reusable/';
import { DURABLE, PERISHABLE } from 'constants/inventory';
import { getImageUrl, capitalizeFirstLetter } from 'utils/';
import { ReactComponent as EditViewIcon } from 'icons/edit_view.svg';

function Record({
  good,
  items,
  upload,
  onArchive,
  isLoading,
  isOnBoarding,
  isMobileView,
}) {
  const tableRef = useRef();
  const { checkBox, checkedIds, onCheckChange } = useCheckTable({});
  const columns = useMemo(() => {
    return [
      checkBox,
      {
        accessorKey: 'id',
        header: 'View/Edit',
        enableColumnFilter: false,
        cell: function ({ row }) {
          return (
            <ActionButton
              title={`Edit ${row.original.item_name}`}
              onClick={onGotToEditItem.bind(null, row.original.id)}
            >
              <EditViewIcon />
            </ActionButton>
          );
        },
      },
      {
        header: 'Picture',
        enableSorting: false,
        enableColumnFilter: false,
        accessorFn: function ({ item_images: images }) {
          let photoUrl = avatar;
          if (Array.isArray(images)) {
            photoUrl = images[0].link;
          }
          if (typeof images === 'string' && !INVALID_URL.includes(images)) {
            photoUrl = images;
          }
          return photoUrl;
        },
        cell: function ({ row: { original }, getValue }) {
          const photoUrl = getValue();
          const { item_name: name = '' } = original;
          return (
            <Avatar
              borderRadius="15%"
              imageAltText={name}
              imageSource={getImageUrl(photoUrl)}
            />
          );
        },
      },
      {
        header: 'Item Name',
        accessorKey: 'item_name',
        cell: function ({ row: { original } }) {
          const { attributes = [], item_name: name } = original;
          return (
            <StyledName>
              {name} <br /> {getAttributeCount(attributes)} attributes
            </StyledName>
          );
        },
      },
      {
        header: 'Qty',
        accessorKey: 'quantity',
        cell: function ({ row: { original } }) {
          const { quantity, attributes, conditions } = original;
          const ordering = getOrderingCount(quantity, attributes, conditions);
          return (
            <StyledName>
              {quantity} <br /> <>{`${ordering} available for orders`}</>
            </StyledName>
          );
        },
      },
      {
        header: 'Category',
        accessorFn: function ({ category }) {
          return capitalizeFirstLetter(category?.category);
        },
      },
      {
        header: 'Tag',
        cell: function ({ row: { original } }) {
          const { tagcolor = '', tagname } = original.tags || {};
          return <StyledItemTag backColor={tagcolor}>{tagname}</StyledItemTag>;
        },
        accessorFn: function ({ tags }) {
          const { tagname = '' } = tags || {};
          return tagname;
        },
      },
      {
        header: 'Warehouse',
        accessorFn: function ({ warehouse }) {
          return warehouse?.name;
        },
      },
      {
        header: 'Value',
        accessorKey: 'purchase_price',
      },
    ];
  }, [onCheckChange]);

  const activeItems = useMemo(() => {
    return items.filter(function (item) {
      return ![disabled].includes(item?.status);
    });
  }, [items]);

  const expiredItems = useMemo(() => {
    const today = new Date().getTime();
    return items.filter(function (item) {
      const {
        itemState,
        item_type: itemType,
        expiry_date: perishableExpiry,
        warranty_expiry: durableExpiry,
      } = item;
      const expiredStatus = ['expired'].includes(itemState?.name.toLowerCase());
      if (itemType === DURABLE) {
        if (durableExpiry) {
          const expiry = new Date(durableExpiry).getTime();
          return today > expiry || expiredStatus;
        }
        return false;
      }
      if (itemType === PERISHABLE) {
        if (perishableExpiry) {
          const expiry = new Date(perishableExpiry).getTime();
          return today > expiry || expiredStatus;
        }
        return false;
      }
    });
  }, [items]);

  function onArchiveClick() {
    onArchive(checkedIds);
  }

  /**
   * @param {[]} attributes
   */
  function getAttributeCount(attributes) {
    if (attributes && attributes.length) {
      const uniqueNames = new Set();
      attributes.forEach(function (attribute) {
        uniqueNames.add(attribute.attribute_name);
      });
      return uniqueNames.size;
    }
    return 0;
  }

  function getOrderingCount(quantity, attributes, conditions) {
    if (attributes && attributes.length > 0) {
      const active = attributes.filter(function ({ quantity, condition }) {
        return quantity > 0 && condition === good?.id;
      });
      return active.length;
    }
    if (conditions) {
      const obj = conditions.conditions;
      const entries = Object.entries(obj);
      const active = entries.filter(function ([key, value]) {
        return value > 0 && key === good?.id;
      });
      return active.length > 0 ? active[0][1] : 0;
    }
    return quantity;
  }

  function HeaderButton({ exportButton }) {
    return (
      <TableButton
        caption="Add Item"
        checkedIds={checkedIds}
        exportButton={exportButton}
        isMobileView={isMobileView}
        isOnBoarding={isOnBoarding}
        onImportClick={onGotToImport}
        onAddClick={onGotToInventory}
        onArchiveClick={onArchiveClick}
      />
    );
  }

  function onGotToImport() {
    history.push(IMPORT_PATH);
  }

  function onGotToInventory() {
    history.push(`${ITEM_PATH}#general`);
  }

  function onGotToEditItem(id) {
    const path = ITEM_EDIT_PATH.replace(':id', id);
    history.push(`${path}#general`);
  }

  const options = [
    {
      name: 'all',
      caption: 'All Items',
      children: (
        <>
          <StyledListWrapper>
            {isLoading && !activeItems?.length > 0 ? (
              <Animation />
            ) : (
              <StyledTableWrapper>
                <Table
                  width={95}
                  centerHeader
                  columns={columns}
                  data={activeItems}
                  tableRef={tableRef}
                  exportName="inventory"
                  headComponent={<HeaderButton />}
                  noDataMessage="No Inventory Items"
                />
              </StyledTableWrapper>
            )}
          </StyledListWrapper>
        </>
      ),
    },
    {
      name: 'expired',
      caption: 'Expired',
      children: (
        <StyledListWrapper>
          {isLoading && !expiredItems?.length > 0 ? (
            <Animation />
          ) : (
            <StyledTableWrapper>
              <Table
                width={95}
                centerHeader
                columns={columns}
                data={expiredItems}
                tableRef={tableRef}
                exportName="expired-inventory"
                headComponent={<HeaderButton />}
                noDataMessage="No Expired Inventory Items"
              />
            </StyledTableWrapper>
          )}
        </StyledListWrapper>
      ),
    },
    {
      name: 'uploaded',
      caption: 'Items with Missing Info',
      children: (
        <StyledListWrapper>
          {upload.isLoading && !upload?.data?.length > 0 ? (
            <Animation />
          ) : (
            <StyledTableWrapper>
              <Table
                width={95}
                centerHeader
                columns={columns}
                data={upload?.data}
                tableRef={tableRef}
                exportName="uploaded-inventory"
                headComponent={<HeaderButton />}
                noDataMessage="No Inventory Items With Missing Info"
              />
            </StyledTableWrapper>
          )}
        </StyledListWrapper>
      ),
    },
  ];

  return <Tab options={options} marginRight={25} />;
}

export default Record;
